import {
  ApiError,
  clearBounceAddress,
  getBounceReport,
  getBounceStatus,
  listBounceAddresses,
  listBounceReports,
  type AddressStatus,
  type BounceReport,
  type BounceStatus,
} from "@/api";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { formatDateTime } from "@/lib/format";
import { BOUNCE_KIND_LABEL, bounceCategoryLabel } from "@/lib/labels";
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

type Tab = "addresses" | "reports";

const PAGE = 100;

function StatusLine({ status }: { status: BounceStatus | null }) {
  if (!status) return <p className="text-xs text-muted">Status wird geladen…</p>;
  if (!status.configured) {
    return (
      <p className="text-xs text-muted">
        Postfach-Abruf nicht konfiguriert. Rückläufer werden nicht ausgewertet.
      </p>
    );
  }
  return (
    <div className="space-y-0.5 text-xs text-muted">
      <p>
        Letzter erfolgreicher Abruf:{" "}
        {status.last_ok_at ? formatDateTime(status.last_ok_at) : "noch keiner"}
        {status.last_ok_at ? ` · ${status.last_fetched} Mails` : null} · alle {Math.round(status.poll_seconds / 60)} min
        {status.verp ? " · VERP aktiv" : ""}
      </p>
      {status.last_error ? <p className="text-danger">Fehler beim Abruf: {status.last_error}</p> : null}
    </div>
  );
}

function AddressesTab() {
  const [items, setItems] = useState<AddressStatus[] | null>(null);
  const [total, setTotal] = useState(0);
  const [invalidOnly, setInvalidOnly] = useState(true);
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    listBounceAddresses({ invalid_only: invalidOnly, q: q.trim() || undefined, limit: PAGE, offset })
      .then((data) => {
        setItems(data.items);
        setTotal(data.total);
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : "Adressen konnten nicht geladen werden.");
      });
  }, [invalidOnly, q, offset]);

  useEffect(() => {
    load();
  }, [load]);

  async function clear(address: string) {
    setBusy(address);
    try {
      await clearBounceAddress(address);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Vermerk konnte nicht zurückgesetzt werden.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <input
          value={q}
          onChange={(event) => {
            setQ(event.target.value);
            setOffset(0);
          }}
          placeholder="Adresse suchen"
          className="w-64 rounded-md border border-hairline bg-surface px-2 py-1 text-sm"
        />
        <label className="flex items-center gap-1.5 text-xs">
          <input
            type="checkbox"
            checked={invalidOnly}
            onChange={(event) => {
              setInvalidOnly(event.target.checked);
              setOffset(0);
            }}
          />
          nur „funktioniert nicht“
        </label>
        <span className="ml-auto text-xs tabular-nums text-muted">{total} Adressen</span>
      </div>
      <p className="mb-3 text-[11px] text-muted">
        Der Vermerk ist nur ein Hinweis: Die Adresse bleibt im Empfängerkreis, solange sie in der Quelle steht.
      </p>
      {error ? <p className="mb-3 text-sm text-danger">{error}</p> : null}
      {items === null && !error ? (
        <div className="h-24 animate-pulse rounded-md bg-surface-subtle" />
      ) : null}
      {items && items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-hairline bg-surface px-6 py-12 text-center text-sm text-muted">
          Keine Adressen mit Rückläufern.
        </div>
      ) : null}
      {items && items.length > 0 ? (
        <div className="overflow-x-auto rounded-lg border border-hairline">
          <table className="w-full text-left text-xs">
            <thead className="bg-surface-subtle text-muted">
              <tr>
                <th className="px-3 py-2 font-medium">Adresse</th>
                <th className="px-3 py-2 font-medium">Rückläufer</th>
                <th className="px-3 py-2 font-medium">Letzter Grund</th>
                <th className="px-3 py-2 font-medium">Letzter Rückläufer</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.address} className="border-t border-hairline align-top">
                  <td className="px-3 py-2 font-mono">{row.address}</td>
                  <td className="px-3 py-2 tabular-nums">
                    {row.bounces}
                    {row.consecutive !== row.bounces ? (
                      <span className="text-muted"> ({row.consecutive} seit Zurücksetzen)</span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">
                    {bounceCategoryLabel(row.last_category)}
                    {row.last_status_code ? (
                      <span className="ml-1 font-mono text-muted">{row.last_status_code}</span>
                    ) : null}
                    {row.last_diagnostic ? (
                      <div className="mt-0.5 max-w-xs truncate text-[10px] text-muted" title={row.last_diagnostic}>
                        {row.last_diagnostic}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-muted">
                    {row.last_bounce_at ? formatDateTime(row.last_bounce_at) : "—"}
                  </td>
                  <td className="px-3 py-2">
                    {row.invalid ? <Badge tone="danger">funktioniert nicht</Badge> : <Badge tone="muted">beobachtet</Badge>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {row.invalid || row.consecutive > 0 ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy === row.address}
                        onClick={() => void clear(row.address)}
                      >
                        Vermerk zurücksetzen
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <Pager total={total} offset={offset} onChange={setOffset} />
    </div>
  );
}

function Pager({ total, offset, onChange }: { total: number; offset: number; onChange: (n: number) => void }) {
  if (total <= PAGE) return null;
  return (
    <div className="mt-3 flex items-center justify-end gap-2 text-xs text-muted">
      <Button size="sm" variant="ghost" disabled={offset === 0} onClick={() => onChange(Math.max(0, offset - PAGE))}>
        Zurück
      </Button>
      <span className="tabular-nums">
        {offset + 1}–{Math.min(offset + PAGE, total)} von {total}
      </span>
      <Button size="sm" variant="ghost" disabled={offset + PAGE >= total} onClick={() => onChange(offset + PAGE)}>
        Weiter
      </Button>
    </div>
  );
}

function ReportsTab({
  eventId,
  messageId,
  initialKind,
  onClearScope,
}: {
  eventId?: string;
  messageId?: string;
  initialKind?: string;
  onClearScope: () => void;
}) {
  const [items, setItems] = useState<BounceReport[] | null>(null);
  const [total, setTotal] = useState(0);
  const [unmatched, setUnmatched] = useState(false);
  const [kind, setKind] = useState(initialKind ?? "");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState<string | null>(null);
  const [raw, setRaw] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listBounceReports({
      kind: kind || undefined,
      unmatched,
      q: q.trim() || undefined,
      eventId,
      messageId,
      limit: PAGE,
      offset,
    })
      .then((data) => {
        setItems(data.items);
        setTotal(data.total);
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : "Meldungen konnten nicht geladen werden.");
      });
  }, [kind, unmatched, q, offset, eventId, messageId]);

  const scopeTitle = items?.find((report) => report.event_title)?.event_title;

  async function toggle(id: string) {
    if (open === id) {
      setOpen(null);
      return;
    }
    setOpen(id);
    if (raw[id] === undefined) {
      try {
        const full = await getBounceReport(id);
        setRaw((prev) => ({ ...prev, [id]: full.raw ?? "" }));
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "Rohtext konnte nicht geladen werden.");
      }
    }
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <input
          value={q}
          onChange={(event) => {
            setQ(event.target.value);
            setOffset(0);
          }}
          placeholder="Adresse, Betreff oder Grund suchen"
          className="w-64 rounded-md border border-hairline bg-surface px-2 py-1 text-sm"
        />
        <select
          value={kind}
          onChange={(event) => {
            setKind(event.target.value);
            setOffset(0);
          }}
          className="rounded-md border border-hairline bg-surface px-2 py-1 text-sm"
        >
          <option value="">Alle Arten</option>
          {Object.entries(BOUNCE_KIND_LABEL).map(([id, label]) => (
            <option key={id} value={id}>
              {label}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-xs">
          <input
            type="checkbox"
            checked={unmatched}
            onChange={(event) => {
              setUnmatched(event.target.checked);
              setOffset(0);
            }}
          />
          nicht zugeordnet
        </label>
        <span className="ml-auto text-xs tabular-nums text-muted">{total} Meldungen</span>
      </div>
      {eventId || messageId ? (
        <div className="mb-3 flex items-center gap-2 text-xs">
          <span className="inline-flex items-center gap-1 rounded-md bg-accent-dim px-2 py-1 text-accent">
            {messageId ? "Mail aus Vorgang" : "Vorgang"}: {scopeTitle || "…"}
            <button
              type="button"
              onClick={() => {
                setOffset(0);
                onClearScope();
              }}
              aria-label="Filter auf Vorgang entfernen"
              className="ml-1 rounded px-1 hover:bg-accent/20"
            >
              ×
            </button>
          </span>
        </div>
      ) : null}
      {error ? <p className="mb-3 text-sm text-danger">{error}</p> : null}
      {items === null && !error ? (
        <div className="h-24 animate-pulse rounded-md bg-surface-subtle" />
      ) : null}
      {items && items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-hairline bg-surface px-6 py-12 text-center text-sm text-muted">
          Keine Meldungen.
        </div>
      ) : null}
      {items && items.length > 0 ? (
        <ul className="divide-y divide-hairline overflow-hidden rounded-lg border border-hairline bg-surface">
          {items.map((report) => (
            <li key={report.id}>
              <button
                type="button"
                onClick={() => void toggle(report.id)}
                className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-left hover:bg-surface-subtle"
              >
                <span className="w-36 shrink-0 text-[11px] tabular-nums text-muted">
                  {formatDateTime(report.received_at)}
                </span>
                <Badge tone={report.kind === "failed" ? "danger" : report.kind === "delayed" ? "warn" : "muted"}>
                  {BOUNCE_KIND_LABEL[report.kind] ?? report.kind}
                </Badge>
                {report.category ? <span className="text-xs">{bounceCategoryLabel(report.category)}</span> : null}
                {report.status_code ? (
                  <span className="font-mono text-[11px] text-muted">{report.status_code}</span>
                ) : null}
                <span className="min-w-0 flex-1 truncate font-mono text-xs">{report.address ?? "—"}</span>
                {report.delivery_id ? (
                  report.event_id ? (
                    <Link
                      to={`/events/${report.event_id}`}
                      onClick={(event) => event.stopPropagation()}
                      className="max-w-[16rem] truncate text-xs text-accent hover:underline"
                    >
                      {report.event_title || "Ohne Titel"}
                    </Link>
                  ) : null
                ) : (
                  <Badge tone="warn">nicht zugeordnet</Badge>
                )}
              </button>
              {open === report.id ? (
                <div className="space-y-1 bg-surface-subtle px-3 py-2">
                  {report.diagnostic ? (
                    <p className="text-[11px] text-muted">{report.diagnostic}</p>
                  ) : null}
                  <p className="text-[11px] text-muted">
                    Betreff: {report.subject || "—"} · Von: {report.from_header || "—"} · Zuordnung: {report.matched_by}
                  </p>
                  <details open>
                    <summary className="cursor-pointer text-[11px] text-muted">Rohtext</summary>
                    <pre className="mt-1 max-h-96 overflow-auto whitespace-pre-wrap break-all text-[11px] text-muted">
                      {raw[report.id] ?? "Laden…"}
                    </pre>
                  </details>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      <Pager total={total} offset={offset} onChange={setOffset} />
    </div>
  );
}

export function BouncesPage() {
  // The archive links here with ?tab=reports&kind=failed&event=…[&message=…].
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(params.get("tab") === "reports" ? "reports" : "addresses");
  const eventId = params.get("event") ?? undefined;
  const messageId = params.get("message") ?? undefined;
  const [status, setStatus] = useState<BounceStatus | null>(null);

  useEffect(() => {
    getBounceStatus()
      .then(setStatus)
      .catch(() => setStatus(null));
  }, []);

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-4">
      <div className="mb-3">
        <h1 className="text-base font-semibold">Rückläufer</h1>
        <StatusLine status={status} />
      </div>
      <div className="mb-3 flex gap-1">
        {(
          [
            ["addresses", "Adressen"],
            ["reports", "Meldungen"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={cn(
              "rounded-md px-2 py-1 text-sm",
              tab === id ? "bg-accent-dim text-accent" : "text-muted hover:bg-surface-subtle hover:text-ink",
            )}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === "addresses" ? (
        <AddressesTab />
      ) : (
        <ReportsTab
          eventId={eventId}
          messageId={messageId}
          initialKind={params.get("kind") ?? undefined}
          onClearScope={() => setParams({ tab: "reports" })}
        />
      )}
    </div>
  );
}
